import { FacetCount, ProductDocument } from '.';

export enum RecommendationType {
  ADS = 'ADS',
  AGEING = 'AGEING',
  BASKET = 'BASKET',
  BOUGHT_TOGETHER = 'FREQUENTLY_BOUGHT_TOGETHER',
  BRANDS = 'BRANDS',
  CATEGORIES = 'CATEGORIES',
  FRESHFOOD = 'FRESH_FOOD',
  NEW_ARRIVALS = 'NEW_ON_BOARDED',
  RECOMMENDED_FOR_YOU = 'RECOMMENDED_FOR_YOU',
  SEARCH_START_TOPPICKS = 'SEARCH_START_TOPPICKS',
  TOPPICKS = 'TOP_PICKS',
}

export enum ApiRecommendationType {
  CUSTOM = 'CUSTOM',
}

export type RecommendationsResponse = {
  algorithm?: string;
  personalised?: boolean;
  recommendationType?: string;
  recommendationVariant?: string;
  totalCount: number;
  products: ProductDocument[];
  recommendations?: ProductDocument[];
  facetCount: FacetCount[];
  total?: number;
};

export type RecommendationCarouselProps = {
  type: string;
  lang: string;
  expandedProductId?: string | null;
  expandedIndex?: number | null;
  setExpandedIndex?: (value: number | null) => void;
  isNative: boolean;
  onDismiss?: (rowEndIndex: number | null) => void;
  setPlpRecommendationHeight?: (height: number) => void;
};

export type RecommendationSeeAllParams = {
  page: number;
  size: number;
  pid?: string;
  brand?: string;
  diversifyResults?: boolean;
  filters?: string;
  sortBy: string;
};

export enum RecommendationTitle {
  TOP_PICKS_FOR_YOU = 'Top Picks For You',
  RECOMMENDED_FOR_YOU = 'Recommended For You',
  BOUGHT_TOGETHER = 'Frequently Bought Together',
  BEST_SELLING = 'Valued from Bestsellings',
  FREQUENTLY_BOUGHT = 'Frequently Bought Items',
  FRESH_FOOD = 'Fresh Must-Buys',
  BRANDS = 'Brand recommendations',
  CATEGORY = 'Other Similar Products',
}

export enum RecommendationVariants {
  RECOMMENDED_FOR_YOU = 'RECOMMENDED_FOR_YOU',
  BASKET = 'BASKET',
  TOP_PICKS = 'TOP_PICKS',
  FREQUENTLY_BOUGHT_TOGETHER = 'FREQUENTLY_BOUGHT_TOGETHER',
  FRESH_FOOD = 'FRESH_FOOD',
  BRANDS = 'brand',
  CATEGORIES = 'category',
}

export enum RecommendedType {
  PRODUCT_BASED = 'PRODUCT_BASED',
  USER_BASED = 'USER_BASED',
  CUSTOM = 'CUSTOM',
}

export const Titles = {
  TOP_PICKS_FOR_YOU: 'Top Picks For You',
  RECOMMENDED_FOR_YOU: 'Recommended For You',
  BOUGHT_TOGETHER: 'Others Also Bought Together',
  BEST_SELLING: 'Valued from Bestsellings',
  FRESH_FOOD: 'Fresh Must-Buys',
  BRANDS: 'Brand recommendations',
  CATEGORY: 'Category recommendations',
};

export enum EndPoint {
  recommendation = 'recommendations',
  search = 'search/document',
}

export const recommendationConfig = {
  [RecommendationType.ADS]: {
    title: Titles.TOP_PICKS_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.TOP_PICKS,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.search,
    pagename: '',
    sourceEvent: '',
  },

  [RecommendationType.AGEING]: {
    title: Titles.TOP_PICKS_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.TOP_PICKS,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.search,
    pagename: '',
    sourceEvent: '',
  },

  [RecommendationType.BASKET]: {
    title: Titles.RECOMMENDED_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.BASKET,
    showLeftIcon: true,
    showSeeAll: true,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: 'basketRecommendation',
    sourceEvent: 'basketRecommendationCarousel',
  },

  [RecommendationType.BOUGHT_TOGETHER]: {
    title: Titles.BOUGHT_TOGETHER,
    recommendedType: RecommendedType.PRODUCT_BASED,
    recommendationVariant: RecommendationVariants.FREQUENTLY_BOUGHT_TOGETHER,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: true,
    endPoint: EndPoint.recommendation,
    pagename: 'boughtTogether',
    sourceEvent: 'boughtTogetherCarousel',
  },

  [RecommendationType.BRANDS]: {
    title: Titles.BRANDS,
    recommendedType: '',
    recommendationVariant: RecommendationVariants.BRANDS,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.search,
    pagename: 'brandRecommendations',
    sourceEvent: 'pdpBrandRecommendationsCarousel',
  },

  [RecommendationType.CATEGORIES]: {
    title: Titles.CATEGORY,
    recommendedType: '',
    recommendationVariant: RecommendationVariants.CATEGORIES,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.search,
    pagename: 'categoryRecommendations',
    sourceEvent: 'pdpCategoryRecommendationsCarousel',
  },

  [RecommendationType.FRESHFOOD]: {
    title: Titles.FRESH_FOOD,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.FRESH_FOOD,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: 'freshRecommendations',
    sourceEvent: 'freshRecommendationsCarousel',
  },

  [RecommendationType.NEW_ARRIVALS]: {
    title: Titles.TOP_PICKS_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.TOP_PICKS,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: '',
    sourceEvent: '',
  },

  [RecommendationType.RECOMMENDED_FOR_YOU]: {
    title: Titles.RECOMMENDED_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.RECOMMENDED_FOR_YOU,
    showLeftIcon: true,
    showSeeAll: true,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: 'recommendedForYou',
    sourceEvent: 'recommendedForYouCarousel',
  },

  [RecommendationType.SEARCH_START_TOPPICKS]: {
    title: Titles.TOP_PICKS_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.TOP_PICKS,
    showLeftIcon: true,
    showSeeAll: true,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: 'topPicksForYou',
    sourceEvent: 'topPicksForYouCarousel',
  },

  [RecommendationType.TOPPICKS]: {
    title: Titles.TOP_PICKS_FOR_YOU,
    recommendedType: RecommendedType.USER_BASED,
    recommendationVariant: RecommendationVariants.TOP_PICKS,
    showLeftIcon: true,
    showSeeAll: false,
    showClose: false,
    endPoint: EndPoint.recommendation,
    pagename: 'topPicksForYou',
    sourceEvent: 'topPicksForYouCarousel',
  },
};
